import { motion } from "framer-motion";
import { Heart, Users, Handshake, ExternalLink, FileText, CheckCircle } from "lucide-react";

const Applications = () => {
  const applicationTypes = [
    {
      id: "adopt-foster",
      icon: Heart,
      title: "Adopt or Foster",
      subtitle: "Give a pet their forever home",
      description: "Whether you're ready to adopt or want to foster, this application is your first step. After submitting, you'll receive a follow-up email with additional questions specific to your application type.",
      fees: [
        "Dogs (over 1 year): $400",
        "Puppies (under 1 year): $500",
        "Board & Train Dogs: $500+",
        "Cats (over 1 year): $100",
        "Kittens (under 1 year): $150",
      ],
      highlights: [
        "All pets are spayed/neutered",
        "All pets are vaccinated",
        "All pets are microchipped",
        "Adoption fees are tax-deductible donations",
      ],
      formUrl: "https://rescuesupportkit.com/rescue/3eyeblind/applications",
      buttonText: "Start Application",
    },
    {
      id: "volunteer",
      icon: Users,
      title: "Volunteer",
      subtitle: "Join our mission",
      description: "We're always looking for passionate volunteers to help with events, transports, fostering support, and more. Your time makes a real difference in the lives of our rescue animals.",
      highlights: [
        "Flexible scheduling",
        "Various roles available",
        "Training provided",
        "Make a direct impact",
      ],
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf1NIEwPuM_JWbNTdCd7Se3libQAyMLKWIaJHBYEvlkVY4drw/viewform?usp=header",
      buttonText: "Apply to Volunteer",
    },
    {
      id: "partner",
      icon: Handshake,
      title: "Become a Partner",
      subtitle: "Business & organization partnerships",
      description: "Partner with Three Eye Blind Animal Rescue to make a larger impact. We work with veterinary clinics, pet stores, trainers, and businesses who share our mission.",
      highlights: [
        "Veterinary partnerships",
        "Corporate sponsorships",
        "Event collaborations",
        "Cross-promotion opportunities",
      ],
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf8SF7YcMlPdqkaf9uNbi3W66EgEXyn8z6yy82Z8erb_3M4ow/viewform",
      buttonText: "Partner With Us",
    },
  ];

  return (
    <div data-testid="applications-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Get Involved
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Applications
            </h1>
            <p className="text-xl text-[#A3A3A3] max-w-2xl mx-auto">
              Ready to make a difference? Choose your path below and join our mission to save lives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Cards */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {applicationTypes.map((app, index) => (
              <motion.div
                key={app.id}
                id={app.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-tactical p-8 lg:p-12"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                  {/* Left - Info */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center">
                        <app.icon className="w-7 h-7 text-[#FFD700]" />
                      </div>
                      <div>
                        <h2 className="font-['Bebas_Neue'] text-4xl text-white">{app.title}</h2>
                        <span className="text-[#FFD700] font-mono text-sm uppercase tracking-widest">
                          {app.subtitle}
                        </span>
                      </div>
                    </div>

                    <p className="text-[#A3A3A3] text-lg mb-8 leading-relaxed">
                      {app.description}
                    </p>

                    {/* Fees (if applicable) */}
                    {app.fees && (
                      <div className="mb-8">
                        <h3 className="font-['Bebas_Neue'] text-xl text-white mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-[#FFD700]" />
                          Adoption Fees
                        </h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {app.fees.map((fee) => (
                            <li key={fee} className="text-[#A3A3A3] text-sm font-mono">
                              {fee}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Highlights */}
                    <div>
                      <h3 className="font-['Bebas_Neue'] text-xl text-white mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                        What to Expect
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {app.highlights.map((highlight) => (
                          <li key={highlight} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                            <span className="w-1.5 h-1.5 bg-[#FFD700]" />
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Right - CTA */}
                  <div className="flex flex-col justify-center items-center lg:items-end">
                    <div className="bg-[#0A0A0A] border border-[#4A5320] p-8 text-center w-full max-w-xs">
                      <span className="font-mono text-[#A3A3A3] text-xs uppercase tracking-widest block mb-4">
                        Ready to Apply?
                      </span>
                      <a
                        href={app.formUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`apply-${app.id}-button`}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        {app.buttonText}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <p className="text-[#A3A3A3] text-xs mt-4">
                        Opens Google Form in new tab
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              How It Works
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white">
              The Application Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Submit", desc: "Complete the online application form" },
              { step: "02", title: "Review", desc: "Our team reviews your application" },
              { step: "03", title: "Meet", desc: "Meet-and-greet with your potential pet" },
              { step: "04", title: "Welcome Home", desc: "Finalize adoption and start your journey" },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <span className="font-['Bebas_Neue'] text-6xl text-[#4A5320]">{item.step}</span>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mt-2 mb-2">{item.title}</h3>
                <p className="text-[#A3A3A3] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-24 border-t border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-6">
            Have Questions?
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Reach out to us directly. We&apos;re here to help you every step of the way.
          </p>
          <a
            href="mailto:threeeyeblindrescue@gmail.com"
            className="btn-secondary inline-flex items-center gap-2"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default Applications;
